import { getVal, setVal } from "oolib";
import { normalizeDisplayValue } from "./filterHiddenDatapoints";
import { getHiddenForPresetAtDepth } from "../WidgetBarChart/utils/getTopBottomDatapoints";

/**
 * SINGLE SOURCE OF TRUTH for a dimension's show/hide state.
 *
 * The state is three INDEPENDENT things, and config stores exactly those —
 * nothing derived:
 *
 *   mode          "manual" | "dynamic" — which of the two drives the chart
 *   manualHidden  the manual selection. ONLY manual actions write it.
 *   dynamicPreset "topN" | "bottomN", with n. ONLY the dynamic tab writes it.
 *
 * What the chart hides is worked out at render time from those plus the CURRENT
 * data (resolveHidden), so a Top-N selection can never go stale against data
 * that has since changed.
 *
 * The previous shape squeezed the first two into one field
 * (`dimNVisibilityPreset` held "all" | "manual" | "topN" | "bottomN", i.e. a
 * mode AND a dynamic sub-mode) and let the dynamic result overwrite the manual
 * selection in `hiddenDimNDatapoints`. That forced a chain of workarounds: a
 * ref to stash the manual list before Dynamic clobbered it, and guesswork on
 * the way back about whether to restore "all" or "manual". The ref died with
 * the lightbox on close, so the selection silently vanished.
 */

export const DEFAULT_N = 3;

export const isDynamicPreset = (p) =>
  p === "topN" || p === "bottomN" || p === "top3" || p === "bottom3";

/**
 * Config paths for one dimension. All are `props.*`, so they can be resolved
 * either from a widgetConfig (config forms) or from the flat props a widget
 * receives at render (see readVisibilityStateFromProps).
 */
export const getVisibilityKeys = ({ dataKeys, dimension, keyOverrides } = {}) => {
  const hiddenKey =
    keyOverrides?.hiddenKey ??
    dataKeys?.[`hiddenDim${dimension}Datapoints`] ??
    `props.hiddenDim${dimension}Datapoints`;

  // Derive the two new keys from the hidden key's own stem rather than assuming
  // the `dimN` namespace. WidgetLineChart overrides its keys to an
  // un-numbered one (`props.hiddenDimDatapoints`), and hardcoding `dimN` here
  // would scatter that widget's state across two namespaces.
  const stem = hiddenKey
    .replace(/^props\./, "")
    .replace(/^hidden/, "")
    .replace(/Datapoints$/, "")
    .replace(/^./, (c) => c.toLowerCase()); // "hiddenDim1Datapoints" -> "dim1"

  return {
    // The manual selection — and nothing else. Dynamic never writes here.
    hiddenKey,
    nKey:
      keyOverrides?.nKey ??
      dataKeys?.[`dim${dimension}VisibilityN`] ??
      `props.dim${dimension}VisibilityN`,
    // Legacy: mode + sub-mode in one slot. Read for back-compat, never written.
    legacyPresetKey:
      keyOverrides?.presetKey ??
      dataKeys?.[`dim${dimension}VisibilityPreset`] ??
      `props.dim${dimension}VisibilityPreset`,
    modeKey: `props.${stem}VisibilityMode`,
    dynamicPresetKey: `props.${stem}DynamicPreset`,
  };
};

/**
 * Read the state, normalising legacy configs on the way through — so nothing
 * needs migrating: old reports resolve correctly, and any edit writes the new
 * shape.
 *
 * One legacy quirk worth knowing: a config saved in a dynamic preset has the
 * COMPUTED set sitting in `hiddenDimNDatapoints` (the old code wrote it there),
 * so on first switch to Manual those values show as the selection. That is what
 * was on screen when it was saved, so it is the honest reading.
 */
export const readVisibilityState = (widgetConfig, keys) => {
  const legacyPreset = getVal(widgetConfig, keys.legacyPresetKey);
  const storedMode = getVal(widgetConfig, keys.modeKey);

  const mode = storedMode || (isDynamicPreset(legacyPreset) ? "dynamic" : "manual");
  // "top3"/"bottom3" are an older spelling of the same thing. Normalise them so
  // the Dynamic tab's radio matches (it compares against topN/bottomN) — the
  // computation already treats them identically.
  const rawDynamicPreset =
    getVal(widgetConfig, keys.dynamicPresetKey) ||
    (isDynamicPreset(legacyPreset) ? legacyPreset : "topN");
  const dynamicPreset =
    rawDynamicPreset === "top3" ? "topN"
    : rawDynamicPreset === "bottom3" ? "bottomN"
    : rawDynamicPreset;
  const n = getVal(widgetConfig, keys.nKey) || DEFAULT_N;

  const raw = getVal(widgetConfig, keys.hiddenKey);
  const manualHidden = Array.isArray(raw) ? raw.map(normalizeDisplayValue) : [];

  return { mode, dynamicPreset, n, manualHidden };
};

/**
 * Same read, for widgets that receive config props flat at render time rather
 * than going through a processRawConfigFn.
 */
export const readVisibilityStateFromProps = (props, dimension) =>
  readVisibilityState({ props }, getVisibilityKeys({ dimension }));

/** What the chart should hide right now, given the state and the current data. */
export const resolveHidden = ({ state, data, dimension, computeHidden }) => {
  if (!state || state.mode !== "dynamic") return state?.manualHidden || [];
  if (!data?.length) return [];
  const compute = computeHidden || ((d, p, n) => getHiddenForPresetAtDepth(d, p, n, dimension));
  return compute(data, state.dynamicPreset, state.n);
};

// ─── Writers. Each writes exactly the one piece it owns. ───

export const writeManualSelection = (w, keys, hidden) => {
  w = setVal(w, keys.hiddenKey, hidden);
  return setVal(w, keys.modeKey, "manual");
};

export const writeDynamicSettings = (w, keys, { dynamicPreset, n }) => {
  w = setVal(w, keys.dynamicPresetKey, dynamicPreset);
  w = setVal(w, keys.nKey, n);
  return setVal(w, keys.modeKey, "dynamic");
};

/**
 * Switch which piece of state drives the chart. The other is untouched, so
 * switching back and forth is lossless — including across a lightbox unmount.
 */
export const writeMode = (w, keys, mode) => setVal(w, keys.modeKey, mode);
